import { createClient } from '@supabase/supabase-js'
import { supabaseConfig, getSupabaseClientConfig } from './supabase-config'
import { SUPABASE_PUBLISHABLE_KEY } from './supabaseKey'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = SUPABASE_PUBLISHABLE_KEY

if (!supabaseUrl || !supabaseKey) {
  throw new Error(
    'Missing Supabase environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY are required. Please check your .env.local file.'
  )
}

export const supabase = createClient(supabaseUrl, supabaseKey, getSupabaseClientConfig())

// Utility functions for common operations
export const supabaseHelpers = {
  // Get current user with error handling
  getCurrentUser: async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser()
      if (error) throw error
      return user
    } catch (error) {
      if (supabaseConfig.environment.enableDebugLogging) {
        console.error('Error getting current user:', error)
      }
      return null
    }
  },

  // Get current session with error handling
  getCurrentSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession()
      if (error) throw error
      return session
    } catch (error) {
      if (supabaseConfig.environment.enableDebugLogging) {
        console.error('Error getting current session:', error)
      }
      return null
    }
  },

  // Check if user is authenticated
  isAuthenticated: async () => {
    const session = await supabaseHelpers.getCurrentSession()
    return !!session
  },

  // Get user's account ID from JWT claims (updated from tenant_id)
  getAccountId: async () => {
    const user = await supabaseHelpers.getCurrentUser()
    return user?.app_metadata?.account_id || null
  },

  // Sign in with better error handling
  signInWithPassword: async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      
      if (error) {
        // Provide user-friendly error messages
        switch (error.message) {
          case 'Invalid login credentials':
            throw new Error('Invalid email or password. Please check your credentials and try again.')
          case 'Email not confirmed':
            throw new Error('Please check your email and click the confirmation link before signing in.')
          case 'Too many requests':
            throw new Error('Too many login attempts. Please wait a moment and try again.')
          default:
            throw new Error(error.message)
        }
      }
      
      return data
    } catch (error) {
      if (supabaseConfig.environment.enableDebugLogging) {
        console.error('Sign in error:', error)
      }
      throw error
    }
  },

  // Sign up with better error handling
  signUpWithPassword: async (email: string, password: string, metadata?: Record<string, unknown>) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      })
      
      if (error) {
        switch (error.message) {
          case 'User already registered':
            throw new Error('An account with this email already exists. Please sign in instead.')
          case 'Password should be at least 6 characters':
            throw new Error('Password must be at least 6 characters long.')
          case 'Signup is disabled':
            throw new Error('New account registration is currently disabled. Please contact support.')
          default:
            throw new Error(error.message)
        }
      }
      
      return data
    } catch (error) {
      if (supabaseConfig.environment.enableDebugLogging) {
        console.error('Sign up error:', error)
      }
      throw error
    }
  },

  // Sign out with error handling
  signOut: async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    } catch (error) {
      if (supabaseConfig.environment.enableDebugLogging) {
        console.error('Sign out error:', error)
      }
      throw error
    }
  },

  // Reset password with error handling.
  // `redirectTo` is computed by the caller (context-aware: workspace vs admin
  // shell) and defaults to the current origin's /reset-password for safety.
  resetPassword: async (email: string, redirectTo?: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectTo ?? `${window.location.origin}/reset-password`,
      })

      if (error) {
        switch (error.message) {
          case 'User not found':
            throw new Error('No account found with this email address.')
          case 'For security purposes, you can only request this once every 60 seconds':
            throw new Error('Please wait 60 seconds before requesting another password reset.')
          default:
            throw new Error(error.message)
        }
      }
    } catch (error) {
      if (supabaseConfig.environment.enableDebugLogging) {
        console.error('Reset password error:', error)
      }
      throw error
    }
  },

  // Upload file to storage
  uploadFile: async (
    bucket: string,
    path: string,
    file: File,
    options?: { upsert?: boolean; onProgress?: (progress: number) => void }
  ) => {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(path, file, {
          upsert: options?.upsert || false,
        })

      if (error) throw error
      return data
    } catch (error) {
      if (supabaseConfig.environment.enableDebugLogging) {
        console.error('File upload error:', error)
      }
      throw error
    }
  },

  // Get file URL from storage
  getFileUrl: (bucket: string, path: string) => {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path)
    
    return data.publicUrl
  },

  // Delete file from storage
  deleteFile: async (bucket: string, path: string) => {
    try {
      const { error } = await supabase.storage
        .from(bucket)
        .remove([path])

      if (error) throw error
    } catch (error) {
      if (supabaseConfig.environment.enableDebugLogging) {
        console.error('File delete error:', error)
      }
      throw error
    }
  }
}
